const HEADING_SELECTOR = 'h1,h2,h3,h4,h5,h6';
const CONTENT_TARGET_SELECTOR = `${HEADING_SELECTOR},a[href]`;

function normalizeWhitespace(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function slugifyAnchorText(text) {
  return normalizeWhitespace(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function splitDashText(text) {
  return normalizeWhitespace(text).split(/\s*(?:[–—]|\s-\s)\s*/).filter(Boolean);
}

function deriveCandidateTexts(text) {
  const queue = [normalizeWhitespace(text)];
  const seenTexts = new Set();
  const candidates = [];

  while (queue.length) {
    const candidate = queue.shift();
    if (!candidate || seenTexts.has(candidate)) {
      continue;
    }

    seenTexts.add(candidate);
    candidates.push(candidate);

    const withoutByline = candidate.replace(/\s+by\s+.+$/i, '').trim();
    if (withoutByline && withoutByline !== candidate) {
      queue.push(withoutByline);
    }

    const dashSegments = splitDashText(candidate);
    if (dashSegments.length > 1) {
      queue.push(dashSegments[0]);
      queue.push(dashSegments[dashSegments.length - 1]);
    }

    const colonIndex = candidate.indexOf(':');
    if (colonIndex > -1) {
      queue.push(candidate.slice(colonIndex + 1).trim());
      queue.push(candidate.slice(0, colonIndex).trim());
    }
  }

  return candidates;
}

function getAnchorTextCandidates(text) {
  const seenSlugs = new Set();
  return deriveCandidateTexts(text).reduce((slugs, candidate) => {
    const slug = slugifyAnchorText(candidate);
    if (slug && !seenSlugs.has(slug)) {
      seenSlugs.add(slug);
      slugs.push(slug);
    }

    return slugs;
  }, []);
}

function findBestAnchorMatch(label, availableTargetSlugs) {
  const targets = Array.from(availableTargetSlugs || []).filter(Boolean);
  const targetSet = new Set(targets);
  const candidates = getAnchorTextCandidates(label);

  for (const candidate of candidates) {
    if (targetSet.has(candidate)) {
      return candidate;
    }
  }

  for (const candidate of candidates) {
    const prefixMatch = targets.find((target) => target.startsWith(`${candidate}-`));
    if (prefixMatch) {
      return prefixMatch;
    }
  }

  return null;
}

function getUniqueAnchorId(preferredId, usedIds, documentRef) {
  let anchorId = preferredId;
  let index = 2;

  while (usedIds.has(anchorId) || documentRef.getElementById(anchorId)) {
    anchorId = `${preferredId}-${index}`;
    index += 1;
  }

  usedIds.add(anchorId);
  return anchorId;
}

function createAnchorElement(documentRef, anchorId) {
  const anchor = documentRef.createElement('a');
  anchor.setAttribute('id', anchorId);
  anchor.className = 'content-target';
  return anchor;
}

function ensureTargetId(element, preferredId, usedIds, documentRef) {
  if (!element || !preferredId) {
    return null;
  }

  const existingElementId = element.getAttribute('id');
  if (existingElementId) {
    usedIds.add(existingElementId);
    return existingElementId;
  }

  const existingContentTarget = element.querySelector(':scope > .content-target[id]');
  if (existingContentTarget) {
    const existingTargetId = existingContentTarget.getAttribute('id');
    usedIds.add(existingTargetId);
    return existingTargetId;
  }

  const anchorId = getUniqueAnchorId(preferredId, usedIds, documentRef);

  if (element.matches(HEADING_SELECTOR)) {
    element.prepend(createAnchorElement(documentRef, anchorId));
    return anchorId;
  }

  const anchor = documentRef.createElement('span');
  anchor.setAttribute('id', anchorId);
  anchor.className = 'content-target';
  element.parentNode.insertBefore(anchor, element);

  return anchorId;
}

function getDirectArticleHeadings(articleContent) {
  return Array.from(articleContent.children).filter((element) =>
    element.matches(HEADING_SELECTOR)
  );
}

function registerTarget(registry, element) {
  const text = normalizeWhitespace(element.textContent);
  const slug = slugifyAnchorText(text);

  if (slug && !registry.has(slug)) {
    registry.set(slug, element);
  }
}

function buildContentTargetRegistry(articleContent) {
  const registry = new Map();

  getDirectArticleHeadings(articleContent).forEach((heading) => {
    registerTarget(registry, heading);
  });

  articleContent.querySelectorAll(CONTENT_TARGET_SELECTOR).forEach((element) => {
    if (!element.closest('.content-index')) {
      registerTarget(registry, element);
    }
  });

  return registry;
}

function initContentAnchors(documentRef = document) {
  const articleContent = documentRef.querySelector('.article-content');
  if (!articleContent) {
    return;
  }

  const usedIds = new Set(
    Array.from(documentRef.querySelectorAll('[id]'))
      .map((element) => element.getAttribute('id'))
      .filter(Boolean)
  );

  getDirectArticleHeadings(articleContent).forEach((heading) => {
    const slug = slugifyAnchorText(heading.textContent);
    ensureTargetId(heading, slug, usedIds, documentRef);
  });

  const targetRegistry = buildContentTargetRegistry(articleContent);
  const availableTargetSlugs = new Set(targetRegistry.keys());

  documentRef.querySelectorAll('.content-index a').forEach((link) => {
    const linkText = normalizeWhitespace(link.textContent);
    const matchedSlug = findBestAnchorMatch(linkText, availableTargetSlugs);

    if (matchedSlug && targetRegistry.has(matchedSlug)) {
      const targetId = ensureTargetId(
        targetRegistry.get(matchedSlug),
        matchedSlug,
        usedIds,
        documentRef
      );

      if (targetId) {
        link.setAttribute('href', `#${targetId}`);
        return;
      }
    }

    const fallbackSlug = slugifyAnchorText(linkText);
    if (fallbackSlug) {
      link.setAttribute('href', `#${fallbackSlug}`);
    }
  });
}

// Use ESM `export` to stay consistent with the rest of the bundle (app.js,
// util.js, charts.js …). The @wordpress Babel preset injects ESM helper
// imports (for…of / iterator helpers) into this file, so webpack already
// classifies it as a harmony module and makes `module.exports` read-only;
// `module.exports = {...}` therefore threw at runtime in production
// ("Cannot assign to read only property 'exports'") and aborted the whole
// bundle. Named `export` lets webpack wire the bindings the way app.js's
// `import { initContentAnchors }` expects.
export {
  findBestAnchorMatch,
  getAnchorTextCandidates,
  initContentAnchors,
  slugifyAnchorText,
};
